import { createElement, FC, forwardRef, memo, ReactNode, useCallback } from "react";
import { GroupBase, SelectInstance } from "react-select";
import type { CreatableProps } from "react-select/creatable";
import Creatable from "react-select/creatable";
import type { GetOptionLabel } from "react-select/dist/declarations/src/types";
import { withAsyncPaginate } from "react-select-async-paginate";
import { GraphQLSelectProps } from "./GraphQLSelect";

const CreatableAsyncPaginate = withAsyncPaginate(Creatable);

export function withCreatableGraphQLSelect<
  Option = unknown,
  IsMulti extends boolean = boolean,
  Additional = {},
  Group extends GroupBase<Option> = GroupBase<Option>,
  R extends SelectInstance<Option, IsMulti, Group> = SelectInstance<Option, IsMulti, Group>,
  TProps extends GraphQLSelectProps<Option, IsMulti, Additional, Group, R> = GraphQLSelectProps<Option, IsMulti, Additional, Group, R>,
>(
  component: FC<TProps>,
  defaultGetNewOptionData: (inputValue: string, optionLabel: ReactNode) => Option,
  defaultGetOptionLabel?: GetOptionLabel<Option>,
): FC<TProps & CreatableProps<Option, IsMulti, Group>> {
  return memo(forwardRef<any, any>(({ getOptionLabel, ...props }: TProps, ref: any) => {
    const wrappedGetOptionLabel = useCallback((option: Option & { __label: ReactNode }) => {
      return option?.__label ?? getOptionLabel?.(option) ?? defaultGetOptionLabel?.(option);
    }, [ getOptionLabel ]);

    const wrappedGetNewOptionData = (inputValue: string, optionLabel: ReactNode) => ({
      __label: optionLabel,
      ...defaultGetNewOptionData(inputValue, optionLabel),
    });

    return createElement(
      component,
      {
        selectComponent: CreatableAsyncPaginate,
        createOptionPosition: "first",
        getOptionLabel: wrappedGetOptionLabel,
        getNewOptionData: wrappedGetNewOptionData,
        ...props,
        ref,
      } as TProps & CreatableProps<Option, IsMulti, Group>,
    );
  }) as FC<TProps & CreatableProps<Option, IsMulti, Group>>);
}